import type { OrchestratedAccountBids } from '@b-stock/fe-core/bstock-next/queries'

import { getLotCategories } from '@/buyer-portal/helpers/lot/categories'
import type { ApiCategory } from '@/components/personalizedCategoryCarousel/apiCategories'
import { mapBuyingMerchandiseToCategories } from '@/components/personalizedCategoryCarousel/mapBuyingMerchandiseToCategories'
import { getBidPseudoStatus } from '@/helpers/getBidPseudoStatus'

type LosingBidCategory = {
  category: ApiCategory | undefined
  excludeListingIds: string[]
}

/**
 * The category to search for Better Odds candidates in, from the buyer's own
 * bids — the same top outbid row the dashboard card shows. Pure and
 * isomorphic (no hooks, no browser APIs) so both BetterOddsCarouselSlot
 * (client) and HomeDashboardBand's server-side prefetch can derive the exact
 * same category from the exact same bids data — one definition, not two
 * copies that can drift.
 */
export const deriveLosingBidCategory = (
  data: OrchestratedAccountBids | undefined
): LosingBidCategory => {
  const bids = data?.bids ?? []

  const losingBid = bids.find((bid) => {
    const status = getBidPseudoStatus(bid)
    // Matches the dashboard card's own "OUTBID" definition (deriveBidRows.ts)
    // so this picks up the same top row the buyer actually sees — including
    // the edge case where an outbid auction/order got canceled. NO_BID is
    // deliberately not included: getBidPseudoStatus can't return it from
    // bids/unique data (only real bids ever reach this), unlike on
    // watchlist cards where that status is reachable.
    return status === 'LOSING' || status === 'LOST' || status === 'LOST_CANCEL'
  })

  const excludeListingIds = bids
    .map((bid) => bid.listing?._id)
    .filter((id): id is string => !!id)

  const category = losingBid
    ? mapBuyingMerchandiseToCategories(
        getLotCategories(losingBid.listing?.lot)
      )[0]
    : undefined

  return { category, excludeListingIds }
}
